/**
 * Verticals — the assessment vocabulary, one per industry.
 *
 * This is the contract that makes TalentHop industry-independent. Until now a requisition picked
 * a `Scope` of hr | technical | skillset, and the questions behind those names were literal
 * software-engineering questions ("design an API rate limiter"). A school teacher could be asked
 * to design a rate limiter, because the fallback bank had nowhere else to go.
 *
 * A vertical owns what "good" means for a family of roles: its competencies, their relative
 * weights, the rubric anchors a judge scores against, and the fallback questions used whenever
 * generation is unavailable. A requisition picks ONE vertical; everything downstream reads from
 * it. There is no global bank left, so software leakage is impossible by construction.
 *
 * VERSION PINNING. A requisition stamps both the vertical key AND the vertical's `version`. An
 * owner re-weighting a vertical next quarter must not silently change what a score from last
 * quarter meant — the anchors are the audit trail if a candidate disputes a result. Any
 * competency or weight edit bumps `version`, and mints an EDITION: an immutable snapshot of the
 * whole competency set under that number (`vertical_editions`, migration 0042). Before 0042 the
 * pinned integer resolved to nothing — only the CURRENT rows were ever stored — so it rendered as
 * provenance while substantiating nothing. `VerticalEdition` below is what it resolves to now.
 *
 * The rows live in `verticals` / `vertical_competencies` (migration 0037), which is also where
 * the six system verticals are seeded. Everything in this file is PURE: no database, no network.
 */
import { z } from "zod";
/**
 * The one useful half of the old `Scope`: whether a competency is probed through lived experience
 * or through doing the work. Every vertical has both — a teaching interview has behavioural
 * questions AND subject/practical ones. Scope's other job, naming the domain, is now the
 * vertical's.
 */
export const COMPETENCY_KINDS = ["behavioural", "practical"];
/**
 * The floor. Fallback NEVER goes to software: when a vertical cannot be resolved, has no seeded
 * questions, or a requisition predates verticals entirely, callers land here — a vertical whose
 * competencies are sensible for ANY job. Seeded by migration 0037 and asserted by verticals.test.ts.
 */
export const DEFAULT_VERTICAL_KEY = "general";
/**
 * How a competency's kind renders as an interview question. The spellings differ on purpose:
 * `QuestionKind` is the stored vocabulary in interview_questions.kind and predates this file
 * (US "behavioral"), while CompetencyKind is new owner-facing copy (British). Bridging here keeps
 * the mismatch in exactly one place instead of scattered `=== "behavioural" ? ...` ternaries.
 */
export const QUESTION_KIND_FOR_COMPETENCY = {
    behavioural: "behavioral",
    practical: "technical",
};
// ── Practical exercises ───────────────────────────────────────────────────────
/**
 * The artefact family an exercise is built from. This is the DISCRIMINANT, and it is deliberately
 * not called "code": asking a candidate to DO the work rather than describe it is industry-
 * independent, and the slot is meant to hold a lesson plan to critique or a customer email to
 * answer just as naturally as a function to write.
 *
 * An unknown type is REFUSED rather than rendered — the regression this whole model exists to undo
 * was shipping a path nothing could reach. Adding a family means three things, all of them small:
 * a member here, its entry in EXERCISE_QUESTION_KIND (which needs a `kind` the interview_questions
 * CHECK constraint allows), and a renderer in the candidate UI. Skip any one of them and the
 * exercise is dead weight.
 *
 * `artefact` is the family 0039 said the slot was for and left unbuilt: a handover note, a job
 * advertisement, a lesson plan, a discovery-call transcript. Migration 0057 authored one per
 * vertical, and filing them under `code` was the alternative — which would have written "this is
 * code" into the stored exercise of every nurse, teacher and shift lead on the platform, exactly
 * the class of lie the rate-limiter question was.
 */
export const EXERCISE_TYPES = ["code", "artefact"];
/** Option labels, shared by the candidate UI, the review screen and the judge attachment so the
 *  three surfaces can never disagree about what "option B" means. */
export const OPTION_LETTERS = ["A", "B", "C", "D"];
/**
 * The usable choice set of a critique (or of a served question carrying the same two fields), or
 * null when it has none / a half-authored one. One predicate for every reader — the UI deciding
 * whether to render buttons, the API deciding whether a pick is accepted, the validator — so a
 * critique with, say, options but a correctIndex pointing past them degrades everywhere at once
 * to an open explain-aloud question rather than to a question no pick can ever get right.
 */
export function choiceOf(c) {
    const options = c?.options;
    const correctIndex = c?.correctIndex;
    if (!Array.isArray(options) || typeof correctIndex !== "number")
        return null;
    if (options.length < VERTICAL_LIMITS.minChoiceOptions || options.length > VERTICAL_LIMITS.maxChoiceOptions) {
        return null;
    }
    if (!Number.isInteger(correctIndex) || correctIndex < 0 || correctIndex >= options.length)
        return null;
    return { options, correctIndex };
}
/**
 * How far the longest and shortest option in a set sit from the set's own mean length, as a
 * fraction of it. `{ under: 0.08, over: 0.11 }` means the shortest option is 8% below the mean and
 * the longest 11% above, so the set is balanced within ±15%.
 *
 * Pure and unopinionated on purpose: the THRESHOLD lives in VERTICAL_LIMITS and the enforcement in
 * verticalExercises.test.ts, so a content wave is held to it while an owner authoring a two-option
 * choice in the editor is not refused for writing a short "Yes".
 */
export function choiceLengthSpread(options) {
    if (!options.length)
        return { under: 0, over: 0 };
    const lens = options.map((o) => o.length);
    const mean = lens.reduce((a, b) => a + b, 0) / lens.length;
    if (mean === 0)
        return { under: 0, over: 0 };
    return { under: 1 - Math.min(...lens) / mean, over: Math.max(...lens) / mean - 1 };
}
/**
 * Whether the best answer is (jointly) the longest option in its set — the shape that makes "pick
 * the longest" a scoring strategy. Joint counts: a tie for longest still rewards the heuristic.
 * A choice this cannot read (half-authored, out of range) answers false, because it will not be
 * served as a choice at all — choiceOf degrades it to an open explain-aloud question.
 */
export function correctIsLongestOption(c) {
    const choice = choiceOf(c);
    if (!choice)
        return false;
    const lens = choice.options.map((o) => o.length);
    return lens[choice.correctIndex] === Math.max(...lens);
}
/** The snapshot to store for a vertical as it currently stands. Pure — the caller owns the write. */
export function editionOf(v) {
    return {
        key: v.key,
        version: v.version,
        name: v.name,
        description: v.description,
        competencies: v.competencies.map((c) => ({
            competencyId: c.competencyId,
            label: c.label,
            weight: c.weight,
            kind: c.kind,
            anchors: c.anchors,
            questions: [...c.questions],
            // `null` rather than `undefined`: this is going into jsonb, where an absent key and an
            // explicit null read back the same, and the readers below already treat null as "none".
            exercise: c.exercise ?? null,
        })),
        protectedTopics: [...(v.protectedTopics ?? [])],
    };
}
/**
 * Read a stored edition's `competencies` jsonb back into the contract.
 *
 * DELIBERATELY LENIENT, and the leniency runs one way only. A snapshot is written from an
 * already-validated vertical, so the shape is trustworthy — but it is also the oldest data in the
 * system, it is jsonb, and it is what a two-year-old disputed score is defended with. Dropping the
 * whole edition because one competency picked up a field a future build writes would send that
 * dispute to the CURRENT anchors, which is precisely the failure editions exist to prevent. So
 * unreadable competencies are skipped individually and everything legible survives.
 *
 * An exercise that fails the contract degrades to "no exercise" — the same rule the live loaders
 * apply (apps/api/src/utils/verticals.ts#toExercise) — because a half-parsed exercise reaches a
 * candidate as a blank editor pane.
 */
export function parseEditionCompetencies(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const item of raw) {
        if (!item || typeof item !== "object")
            continue;
        const c = item;
        const competencyId = typeof c.competencyId === "string" ? c.competencyId.trim() : "";
        if (!competencyId)
            continue;
        const weight = typeof c.weight === "number" && Number.isFinite(c.weight) ? c.weight : 0;
        const kind = c.kind === "behavioural" || c.kind === "practical" ? c.kind : "practical";
        const exercise = c.exercise === null || c.exercise === undefined
            ? null
            : (competencyExerciseSchema.safeParse(c.exercise).data ?? null);
        out.push({
            competencyId,
            label: typeof c.label === "string" && c.label.trim() ? c.label : competencyId,
            weight,
            kind,
            anchors: typeof c.anchors === "string" ? c.anchors : "",
            questions: Array.isArray(c.questions)
                ? c.questions.filter((q) => typeof q === "string" && q.trim().length > 0)
                : [],
            exercise,
        });
    }
    return out;
}
// ── Lookups ───────────────────────────────────────────────────────────────────
export function competencyById(vertical, competencyId) {
    return vertical.competencies.find((c) => c.competencyId === competencyId);
}
/**
 * Weights are RELATIVE — the rubric copy has always said "the scorer normalises" — so a vertical
 * whose weights sum to 93 or 140 still scores sanely. Returns a fraction per competencyId summing
 * to 1, i.e. `sum(score_i × w_i)` IS the composite.
 *
 * Two degenerate cases are handled rather than propagated as NaN: a vertical with no competencies
 * yields `{}`, and one whose weights are all zero or negative yields an equal split. A silently
 * zero-weighted rubric is how every score in an interview quietly becomes 0.
 */
export function normalisedWeights(vertical) {
    const comps = vertical.competencies;
    if (comps.length === 0)
        return {};
    const positive = comps.map((c) => (Number.isFinite(c.weight) && c.weight > 0 ? c.weight : 0));
    const total = positive.reduce((a, b) => a + b, 0);
    const out = {};
    comps.forEach((c, i) => {
        out[c.competencyId] = total > 0 ? (positive[i] ?? 0) / total : 1 / comps.length;
    });
    return out;
}
// ── Deterministic fallback questions ──────────────────────────────────────────
/** FNV-1a 32-bit — same primitive as providers/fakes.ts, so fallbacks stay reproducible. */
function hash(input) {
    let h = 0x811c9dc5;
    for (let i = 0; i < input.length; i++) {
        h ^= input.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}
/**
 * Depth → which half of the exercise is served.
 *
 *   L2 or L3 present → critique — read the artefact, pick the best statement about it where the
 *                      variant carries options (#78), and explain the reasoning ALOUD
 *   L1 only          → none — L1 is ID + screening, it does not sit anyone down to do the work
 *   no levels        → critique, same as above for callers that don't know the requisition
 *
 * L3 used to map to `produce` (type the code, machine-checked). It no longer does: the interview
 * is voice-first, and a typing task in the middle of a spoken conversation punished exactly the
 * candidates the conversation format exists to put at ease. `produce` is RESERVED for a future
 * typed-assessment mode — the depth still exists, resolveExercise still serves it (both when asked
 * for directly and as the fallback where a competency has no critique authored), and the data
 * stays on every exercise. Only the level mapping stopped reaching for it.
 */
export function exerciseDepthForLevels(levels) {
    if (!levels || levels.length === 0)
        return "critique";
    if (levels.includes("L3") || levels.includes("L2"))
        return "critique";
    return "none";
}
/**
 * How each artefact family renders as a stored interview_questions.kind. Every value must be in
 * that column's CHECK constraint (migrations 0003 + 0010) AND have a renderer in the candidate UI,
 * which is why this map is the gate rather than a free-text pass-through.
 *
 * `produce: null` means the family HAS no write half and can never grow one by accident.
 * `ExerciseProduce extends CodeSpec` — a language, a starter and hidden cases fed to a sandbox —
 * so there is no honest way to express "produce a handover note", and resolveExercise refuses
 * rather than reaching for the nearest kind. That refusal is what keeps 0039's safety property
 * true now that eleven non-software verticals carry exercises: a nurse cannot be shown a code
 * editor, and it is no longer merely because she has no exercise to show.
 *
 * Both families critique as `code_reading`. That stored spelling is now wrong for eleven of the
 * twelve, and it stays anyway: the kind is the wire vocabulary that the CHECK constraint, the
 * question planner, the worker's scorer and the candidate renderer all already agree on, and
 * renaming it would churn four shipped scoring paths to fix a string no candidate reads. What a
 * candidate READS is KIND_LABEL in InterviewStep.tsx, and that said "Read the code" over a lesson
 * plan until 0057 — that was the bug worth fixing, and it was fixed there.
 */
export const EXERCISE_QUESTION_KIND = {
    code: { produce: "code", critique: "code_reading" },
    artefact: { produce: null, critique: "code_reading" },
};
/**
 * Resolve a competency's exercise for a given depth, or null when there is nothing to serve —
 * which is the answer for four competencies in five, and for all L1-only work.
 *
 * Falling back across halves rather than dropping the exercise: at L2 with no snippets authored we
 * serve the write task (what the old bank did), and at L3 with no write spec we serve a critique
 * (the old bank dropped it, which was simply a gap). Never inventing one is the invariant that
 * matters — a competency with no exercise gets a normal spoken question.
 */
export function resolveExercise(competency, depth, seedStr) {
    const ex = competency?.exercise;
    if (!ex || depth === "none")
        return null;
    const kinds = EXERCISE_QUESTION_KIND[ex.type];
    if (!kinds)
        return null; // a family this build cannot render → a spoken question, never a blank pane
    const variants = ex.critique ?? [];
    const critique = variants.length > 0 ? variants[hash(`${seedStr}:critique`) % variants.length] : undefined;
    // An empty `cases` list is not a runnable exercise — the sandbox would report a pass rate over
    // nothing, which reads as "all tests passed" to the judge. A family with no write half
    // (`produce: null`) drops one just as firmly: an `artefact` row that somehow carries a CodeSpec
    // is a hand-edit or a bad import, and serving it would put a code editor in front of whoever the
    // vertical belongs to.
    const produceKind = kinds.produce;
    const produce = produceKind && ex.produce && ex.produce.cases.length > 0 ? ex.produce : undefined;
    const useCritique = depth === "critique" ? !!critique : !produce && !!critique;
    if (useCritique && critique) {
        // choiceOf, not the raw fields: a half-authored choice (a lone correctIndex, an index past
        // the options) degrades HERE to an open explain-aloud question, so no downstream reader can
        // serve buttons where no pick could ever be right.
        const choice = choiceOf(critique);
        return {
            kind: kinds.critique,
            codeSpec: null,
            snippet: critique.snippet,
            options: choice?.options ?? null,
            correctIndex: choice?.correctIndex ?? null,
            prompt: critique.prompt,
        };
    }
    if (produce && produceKind) {
        const prompt = produce.prompts[hash(`${seedStr}:produce`) % Math.max(1, produce.prompts.length)];
        return {
            kind: produceKind,
            codeSpec: { language: produce.language, starter: produce.starter, cases: produce.cases },
            snippet: null,
            options: null,
            correctIndex: null,
            prompt: prompt ?? "",
        };
    }
    return null;
}
/**
 * Deterministic fallback pick: round-robin over the vertical's competencies, hash-seeded variant
 * per slot. Same (vertical content, seedStr, count, competencyIds, levels) → identical questions.
 *
 * `competencyIds` narrows to the recruiter's selection; unknown ids are ignored, and an empty or
 * fully-unmatched selection falls back to the whole vertical rather than returning nothing —
 * a candidate with zero questions is a worse failure than a slightly broader interview.
 *
 * `levels` selects which half of a competency's practical exercise is served, if it has one. An
 * exercise is served AT MOST ONCE per competency per call: the artefact is the same every time, so
 * a second slot on the same competency would re-show the identical snippet instead of probing
 * something new — it falls back to that competency's spoken questions.
 */
export function questionsForVertical(vertical, seedStr, count, competencyIds, levels) {
    const wanted = new Set(competencyIds ?? []);
    const scoped = wanted.size > 0
        ? vertical.competencies.filter((c) => wanted.has(c.competencyId))
        : vertical.competencies;
    const pool = (scoped.length > 0 ? scoped : vertical.competencies).filter((c) => c.questions.length > 0);
    if (pool.length === 0 || count <= 0)
        return [];
    const depth = exerciseDepthForLevels(levels);
    const exerciseSpent = new Set();
    const picked = [];
    for (let i = 0; i < count; i++) {
        const comp = pool[i % pool.length];
        if (!comp)
            break; // satisfies noUncheckedIndexedAccess; pool is non-empty here
        if (!exerciseSpent.has(comp.competencyId)) {
            const ex = resolveExercise(comp, depth, `${seedStr}:${comp.competencyId}`);
            if (ex) {
                exerciseSpent.add(comp.competencyId);
                picked.push({
                    competencyId: comp.competencyId,
                    kind: ex.kind,
                    // A produce exercise with no prompts authored still has a task to set, so fall back to
                    // the competency's own question rather than showing an editor above an empty prompt.
                    prompt: ex.prompt || comp.questions[0] || comp.label,
                    codeSpec: ex.codeSpec,
                    snippet: ex.snippet,
                    options: ex.options,
                    correctIndex: ex.correctIndex,
                });
                continue;
            }
        }
        const variant = comp.questions[hash(`${seedStr}:${comp.competencyId}:${i}`) % comp.questions.length];
        picked.push({
            competencyId: comp.competencyId,
            kind: QUESTION_KIND_FOR_COMPETENCY[comp.kind],
            prompt: variant ?? comp.label,
            codeSpec: null,
            snippet: null,
            options: null,
            correctIndex: null,
        });
    }
    return picked;
}
// ── Validation ────────────────────────────────────────────────────────────────
/** Bounds mirrored by the CHECK constraints in migrations 0037/0038 — keep the two in step. */
export const VERTICAL_LIMITS = {
    minCompetencies: 1,
    maxCompetencies: 12,
    minWeight: 1,
    maxWeight: 100,
    minQuestionsPerCompetency: 1,
    maxQuestionsPerCompetency: 12,
    /** Guardrail clauses are prepended to every generation prompt, so the list stays short enough
     *  to actually be read by the model rather than skimmed past. */
    maxProtectedTopics: 8,
    maxProtectedTopicLen: 400,
    /** Exercise bounds — an artefact is shown in full inside one interview pane, not scrolled. */
    maxExerciseArtefactLen: 4000,
    minExerciseCases: 1,
    maxExerciseCases: 20,
    maxExerciseVariants: 6,
    /** Snippet-choice bounds (#78). Two options is the floor for a pick to mean anything; four is
     *  the ceiling because the options are read ALOUD-adjacent (shown beside a spoken question) and
     *  a wall of five statements stops being a choice and starts being a reading test. */
    minChoiceOptions: 2,
    maxChoiceOptions: 4,
    maxChoiceOptionLen: 240,
    /** How far an option may sit from its set's mean length before length itself starts telling a
     *  candidate which one is right (see ExerciseCritique). ±15% is loose enough that a set can be
     *  written naturally and tight enough that "pick the longest" is worthless. Enforced over the
     *  seeded catalogue by verticalExercises.test.ts, not by validateVertical — an owner writing a
     *  short "Yes" against a long "No, because …" in the editor is not authoring a rubric wave. */
    choiceLengthTolerance: 0.15,
    /** The most of the catalogue whose correct answer may be the longest option in its set. Above
     *  this, guessing by length beats not knowing the trade — which is what it did at 9 of 14 before
     *  the fairness pass over 0057. */
    maxShareCorrectIsLongest: 1 / 3,
};
/**
 * DELIVERY WORDS — the fairness rule 0058 exists to enforce, stated once, here, so the migration
 * seed, docs/rubric-wave and the tests cannot each hold a different idea of it.
 *
 * A band may turn on WHAT is present in an answer. It may never turn on HOW that answer sounded.
 * The distinction is not pedantry: an anchor reading "gives a clear enough explanation" hands the
 * judge no content test at all, so the only thing left to grade is the delivery — and on an
 * India-first product the delivery is an accent, a hesitation and a second-language word order.
 * The same answer then scores lower in broken English, which is the defect this whole rubric line
 * of work exists to remove.
 *
 * Two families, and the second is the subtle one:
 *
 *   FORM ADJECTIVES — clear, concise, structured, fluent, coherent, articulate, polished. 0057
 *   caught most of these but left four behind, all in the "communication" competency of their
 *   vertical, which is exactly where a fluency penalty lands.
 *
 *   FOLLOWABILITY TESTS — "in terms the listener could not follow". This reads as a jargon test
 *   and is meant as one, but what it literally asks the judge is whether the listener could FOLLOW
 *   the speaker, and a halting speaker is harder to follow whatever they say. The house form is
 *   the one customer_support_bpo already uses: could not ACT on it, or the jargon went untranslated
 *   — both of which are properties of the words, not of the mouth they came out of.
 *
 * `demeanour` (professional, confident, articulate …) was already asserted over 0057 in
 * verticalExercises.test.ts and is folded in here so there is one list rather than two drifting
 * ones. Not wired into validateVertical on purpose: this is a standard for AUTHORED WAVES, and
 * refusing an owner's hand-typed anchor in the editor because it contains the word "clear" would
 * be a worse product than the one word costs us.
 */
export const ANCHOR_DELIVERY_WORDS = /\b(clear|clearly|clarity|concise|concisely|succinct|structured|unstructured|fluent|fluency|eloquent|coherent|rambling|waffle|verbose|long-winded|articulate|polished|professional|confident|enthusiastic|passionate|mature|personable|likeable|well-presented|charismatic|humble|ego|could not follow|cannot follow|hard to follow|difficult to follow|could follow|in no particular order)\b/i;
/**
 * The delivery words a set of anchors still contains, one per band, as `41-70: structured`.
 * Empty means every band turns on content. Pure — the caller decides whether that is a failure.
 */
export function deliveryWordsIn(anchors) {
    const out = [];
    for (const band of anchors.split(";")) {
        const hit = ANCHOR_DELIVERY_WORDS.exec(band);
        if (hit)
            out.push(`${/^\s*(0-40|41-70|71-100):/.exec(band)?.[1] ?? "?"}: ${hit[0].toLowerCase()}`);
    }
    return out;
}
/**
 * Readable, owner-facing validation for the Verticals editor. Returns an EMPTY array when the
 * vertical is publishable; every string is a complete sentence safe to render in a banner.
 *
 * Deliberately not a zod refinement: the editor needs ALL the problems at once ("weights sum to
 * 93" and "two competencies share an id"), not the first parse failure.
 */
export function validateVertical(vertical) {
    const errors = [];
    const comps = vertical.competencies ?? [];
    const topics = vertical.protectedTopics ?? [];
    if (topics.length > VERTICAL_LIMITS.maxProtectedTopics) {
        errors.push(`A vertical may carry at most ${VERTICAL_LIMITS.maxProtectedTopics} extra guardrail clauses; this one has ${topics.length}.`);
    }
    for (const t of topics) {
        if (t.trim().length < 10) {
            errors.push("Every guardrail clause needs to be a full instruction the model can act on — one or two words won't steer it.");
            break; // one message is enough; the editor lists the offending row itself
        }
    }
    if (vertical.key !== undefined && !/^[a-z][a-z0-9_]{1,62}$/.test(vertical.key)) {
        errors.push(`Key "${vertical.key}" must be lower-case snake_case, starting with a letter (e.g. "software_engineering").`);
    }
    if (comps.length < VERTICAL_LIMITS.minCompetencies) {
        errors.push("A vertical needs at least one competency — there is nothing to assess otherwise.");
    }
    if (comps.length > VERTICAL_LIMITS.maxCompetencies) {
        errors.push(`A vertical may hold at most ${VERTICAL_LIMITS.maxCompetencies} competencies; this one has ${comps.length}.`);
    }
    const seen = new Set();
    for (const c of comps) {
        const id = c.competencyId;
        if (seen.has(id))
            errors.push(`Competency id "${id}" appears more than once; ids must be unique.`);
        seen.add(id);
        if (!/^[a-z][a-z0-9_]{1,62}$/.test(id)) {
            errors.push(`Competency id "${id}" must be lower-case snake_case, starting with a letter.`);
        }
        if (!c.label?.trim())
            errors.push(`Competency "${id}" needs a label.`);
        if (!Number.isInteger(c.weight) || c.weight < VERTICAL_LIMITS.minWeight || c.weight > VERTICAL_LIMITS.maxWeight) {
            errors.push(`Competency "${id}" has weight ${c.weight}; weights are whole numbers from ${VERTICAL_LIMITS.minWeight} to ${VERTICAL_LIMITS.maxWeight}.`);
        }
        if (!c.anchors?.trim()) {
            errors.push(`Competency "${id}" needs rubric anchors — they are what a disputed score is defended with.`);
        }
        if ((c.questions?.length ?? 0) < VERTICAL_LIMITS.minQuestionsPerCompetency) {
            errors.push(`Competency "${id}" needs at least one fallback question, used whenever question generation is unavailable.`);
        }
        if ((c.questions?.length ?? 0) > VERTICAL_LIMITS.maxQuestionsPerCompetency) {
            errors.push(`Competency "${id}" has more than ${VERTICAL_LIMITS.maxQuestionsPerCompetency} fallback questions.`);
        }
        errors.push(...exerciseErrors(id, c.exercise));
    }
    return errors;
}
/**
 * Exercise checks, split out so the messages stay readable. Every one of them describes a way an
 * exercise would reach a candidate as a BROKEN pane — an editor with no tests behind it, a
 * "what does this do?" with nothing shown, a family this build cannot draw.
 */
function exerciseErrors(id, exercise) {
    if (!exercise)
        return []; // no exercise is still the normal case — four competencies in five
    const errors = [];
    const L = VERTICAL_LIMITS;
    if (!EXERCISE_TYPES.includes(exercise.type)) {
        errors.push(`Competency "${id}" has an exercise of type "${exercise.type}", which this build can't present to a candidate. Supported: ${EXERCISE_TYPES.join(", ")}.`);
        return errors; // the rest of the checks are meaningless for a family we can't render
    }
    if (!exercise.produce && !(exercise.critique?.length ?? 0)) {
        errors.push(`Competency "${id}" has an exercise with nothing in it — give it something to build, something to critique, or remove it.`);
    }
    const p = exercise.produce;
    if (p) {
        if (!p.language?.trim())
            errors.push(`Competency "${id}" has a build exercise with no language set.`);
        if ((p.cases?.length ?? 0) < L.minExerciseCases) {
            errors.push(`Competency "${id}" has a build exercise with no test cases — the score would be a pass rate over nothing.`);
        }
        if ((p.cases?.length ?? 0) > L.maxExerciseCases) {
            errors.push(`Competency "${id}" has a build exercise with more than ${L.maxExerciseCases} test cases.`);
        }
        if ((p.prompts?.length ?? 0) < 1) {
            errors.push(`Competency "${id}" has a build exercise with no task statement to show the candidate.`);
        }
        if ((p.starter?.length ?? 0) > L.maxExerciseArtefactLen) {
            errors.push(`Competency "${id}" has starter material longer than ${L.maxExerciseArtefactLen} characters.`);
        }
    }
    const variants = exercise.critique ?? [];
    if (variants.length > L.maxExerciseVariants) {
        errors.push(`Competency "${id}" has more than ${L.maxExerciseVariants} critique variants.`);
    }
    for (const v of variants) {
        if (!v.snippet?.trim()) {
            errors.push(`Competency "${id}" has a critique variant with nothing to show the candidate.`);
        }
        if (!v.prompt?.trim()) {
            errors.push(`Competency "${id}" has a critique variant with no question attached to it.`);
        }
        if ((v.snippet?.length ?? 0) > L.maxExerciseArtefactLen) {
            errors.push(`Competency "${id}" has a critique artefact longer than ${L.maxExerciseArtefactLen} characters.`);
        }
        // Choice checks (#78). Every one of these is a way the buttons would reach a candidate with
        // no pick that can be right, or a "choice" that is not one.
        if ((v.options === undefined) !== (v.correctIndex === undefined)) {
            errors.push(`Competency "${id}" has a critique variant with options but no correct answer marked (or the reverse) — a choice needs both, or neither.`);
        }
        if (v.options !== undefined) {
            if (v.options.length < L.minChoiceOptions || v.options.length > L.maxChoiceOptions) {
                errors.push(`Competency "${id}" has a choice with ${v.options.length} option(s); a choice carries ${L.minChoiceOptions} to ${L.maxChoiceOptions}.`);
            }
            if (new Set(v.options.map((o) => o.trim())).size !== v.options.length) {
                errors.push(`Competency "${id}" has a choice with two identical options — a pick between them means nothing.`);
            }
            if (v.options.some((o) => !o.trim())) {
                errors.push(`Competency "${id}" has a choice with an empty option.`);
            }
            if (v.options.some((o) => o.length > L.maxChoiceOptionLen)) {
                errors.push(`Competency "${id}" has a choice option longer than ${L.maxChoiceOptionLen} characters.`);
            }
            if (v.correctIndex !== undefined &&
                (!Number.isInteger(v.correctIndex) || v.correctIndex < 0 || v.correctIndex >= v.options.length)) {
                errors.push(`Competency "${id}" marks option ${v.correctIndex} as correct, but the choice only has options 0 to ${v.options.length - 1}.`);
            }
        }
    }
    return errors;
}
// ── zod (admin CRUD) ──────────────────────────────────────────────────────────
/** A vertical key or a competency id on the wire — snake_case, so it is safe in urls and jsonb keys. */
export const verticalKeySchema = z
    .string()
    .regex(/^[a-z][a-z0-9_]{1,62}$/, "lower-case snake_case, starting with a letter");
export const competencyIdSchema = z
    .string()
    .regex(/^[a-z][a-z0-9_]{1,62}$/, "lower-case snake_case, starting with a letter");
export const competencyKindSchema = z.enum(COMPETENCY_KINDS);
/**
 * The exercise on the wire, and the same schema the api uses to sanitise the `exercise` jsonb
 * column on read — a hand-edited row that cannot be parsed degrades to a spoken question rather
 * than a half-drawn pane. Bounds mirror VERTICAL_LIMITS and the CHECK in migration 0039.
 */
export const exerciseProduceSchema = z.object({
    language: z.string().min(1).max(40),
    starter: z.string().max(VERTICAL_LIMITS.maxExerciseArtefactLen).default(""),
    cases: z
        .array(z.object({ stdin: z.string().max(2000), expected: z.string().max(2000) }))
        .min(VERTICAL_LIMITS.minExerciseCases)
        .max(VERTICAL_LIMITS.maxExerciseCases),
    prompts: z.array(z.string().min(8).max(600)).min(1).max(VERTICAL_LIMITS.maxExerciseVariants),
});
export const exerciseCritiqueSchema = z
    .object({
    snippet: z.string().min(1).max(VERTICAL_LIMITS.maxExerciseArtefactLen),
    prompt: z.string().min(8).max(600),
    options: z
        .array(z.string().min(1).max(VERTICAL_LIMITS.maxChoiceOptionLen))
        .min(VERTICAL_LIMITS.minChoiceOptions)
        .max(VERTICAL_LIMITS.maxChoiceOptions)
        .optional(),
    correctIndex: z.number().int().min(0).optional(),
})
    // Both-or-neither, index in range, options distinct — the same rules choiceOf() and the
    // owner-facing validator apply, enforced at the wire so a bad write never lands.
    .refine((c) => (c.options === undefined) === (c.correctIndex === undefined), {
    message: "a choice needs both options and a correctIndex, or neither",
})
    .refine((c) => c.options === undefined || c.correctIndex === undefined || c.correctIndex < c.options.length, {
    message: "correctIndex must point at one of the options",
})
    .refine((c) => c.options === undefined || new Set(c.options.map((o) => o.trim())).size === c.options.length, {
    message: "choice options must be distinct",
});
export const competencyExerciseSchema = z
    .object({
    type: z.enum(EXERCISE_TYPES),
    produce: exerciseProduceSchema.optional(),
    critique: z.array(exerciseCritiqueSchema).min(1).max(VERTICAL_LIMITS.maxExerciseVariants).optional(),
})
    .refine((e) => !!e.produce || !!e.critique, {
    message: "an exercise needs something to build or something to critique",
});
export const verticalCompetencyInputSchema = z.object({
    competencyId: competencyIdSchema,
    label: z.string().min(2).max(80),
    weight: z.number().int().min(VERTICAL_LIMITS.minWeight).max(VERTICAL_LIMITS.maxWeight),
    kind: competencyKindSchema,
    anchors: z.string().min(10).max(1000),
    questions: z
        .array(z.string().min(8).max(600))
        .min(VERTICAL_LIMITS.minQuestionsPerCompetency)
        .max(VERTICAL_LIMITS.maxQuestionsPerCompetency),
    // nullable AND optional, and the difference carries meaning on the write path: OMITTED means
    // "leave the stored exercise alone" (no editor ships one yet, so every client omits it and none
    // of them intends to delete a code exercise), explicit NULL means "clear it".
    exercise: competencyExerciseSchema.nullish(),
});
/**
 * The owner-editable surface. `isSystem` and `version` are deliberately absent: a client cannot
 * declare its own vertical a system one, and the version is bumped by the server on every edit —
 * a client that could set it could un-pin an old requisition's audit trail.
 */
export const verticalInputSchema = z.object({
    key: verticalKeySchema,
    name: z.string().min(2).max(80),
    description: z.string().max(400).default(""),
    isActive: z.boolean().default(true),
    sortOrder: z.number().int().min(0).max(999).default(0),
    competencies: z
        .array(verticalCompetencyInputSchema)
        .min(VERTICAL_LIMITS.minCompetencies)
        .max(VERTICAL_LIMITS.maxCompetencies),
    // Defaulted, not required: a client that has never heard of guardrails (or an owner who wants
    // none beyond the universal clause) posts a vertical with no extra clauses, not a 400.
    protectedTopics: z
        .array(z.string().min(10).max(VERTICAL_LIMITS.maxProtectedTopicLen))
        .max(VERTICAL_LIMITS.maxProtectedTopics)
        .default([]),
});
/**
 * Re-pointing an EXISTING requisition at a different vertical (PUT /requisitions/{id}/vertical).
 *
 * Lives beside the vertical contract rather than with the other requisition schemas because the
 * rules it encodes are the vertical's: the target key's shape, and the fact that the competency
 * selection is re-declared from scratch. Ids from the OLD vertical mean nothing in the new one, so
 * an omitted list is "not chosen yet" (exactly as at creation) and never a carry-over.
 *
 * `confirm` is the acknowledgement that existing question plans will be discarded — the API returns
 * 409 `confirm_required` with the count before it will act. It is deliberately not a `force`: the
 * API still refuses outright once any candidate has been scored, and no flag overrides that.
 */
export const repointVerticalSchema = z.object({
    verticalKey: verticalKeySchema,
    competencies: z.array(competencyIdSchema).max(VERTICAL_LIMITS.maxCompetencies).optional(),
    confirm: z.boolean().optional(),
});
//# sourceMappingURL=verticals.js.map